import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Updates the price based on quantity changes.
   * @param {Event} event - The input event.
   */
  updatePrice(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const quantity = parseInt(event.target.value) || 1;

    // Find the product price component in the same section
    const priceComponent = this.closest('.shopify-section')?.querySelector('[data-product-price]');
    if (!priceComponent || !(priceComponent instanceof HTMLElement)) return;

    this.#updatePriceDisplay(priceComponent, quantity);
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

    /**
   * Updates the price display based on quantity.
   * @param {HTMLElement} priceComponent - The price component element.
   * @param {number} quantity - The selected quantity.
   */
  #updatePriceDisplay(priceComponent, quantity) {
    const basePrice = parseFloat(priceComponent.dataset.basePrice || '0') || 0;
    const baseComparePrice = parseFloat(priceComponent.dataset.baseComparePrice || '0') || 0;

    const newPrice = basePrice * quantity;
    const newComparePrice = baseComparePrice * quantity;

    // Update regular price
    const regularPriceEl = priceComponent.querySelector('[data-regular-price]');
    if (regularPriceEl && baseComparePrice > 0) {
      regularPriceEl.textContent = this.#formatMoney(newComparePrice);
    }

    // Update sale price
    const salePriceEl = priceComponent.querySelector('[data-sale-price]');
    if (salePriceEl) {
      salePriceEl.textContent = this.#formatMoney(newPrice);
    }
  }

  /**
   * Formats money using the UX product price component's formatting.
   * @param {number} priceInCents - The price in cents.
   * @returns {string} The formatted price using shop's currency settings.
   */
  #formatMoney(priceInCents) {
    // Find the UX product price component which has the money format template
    const section = this.closest('.shopify-section');
    const uxProductPrice = section?.querySelector('ux-product-price');

    // Check if it's our custom UX product price component with formatMoney method
    if (uxProductPrice && 'formatMoney' in uxProductPrice && typeof uxProductPrice.formatMoney === 'function') {
      return uxProductPrice.formatMoney(priceInCents);
    }

    // Fallback formatting
    return (priceInCents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
    });
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
